/**
 * Chat cards — every in-conversation card the assistant can produce:
 * artifact references, action proposals, ask-forms (details confirm / facts),
 * web-search offers/citations, plus the small shared chrome (ThinkingDots,
 * MsgAction, AskCard shell). Extracted verbatim from AgentPage.jsx (Phase 2
 * chat-core split); behavior-frozen.
 */
import { useEffect, useRef, useState } from 'react';
import {
  FiFileText, FiExternalLink, FiZap, FiCheck, FiChevronRight, FiGlobe, FiGrid, FiDownload, FiAlertCircle,
} from 'react-icons/fi';
import Button from '../components/common/Button';
import HoverPopover from '../components/common/HoverPopover';
import { inputClass, inputSmClass } from '../components/common/FormControls';
import { FactsField, toDocumentValue } from './factsFields';
import { askableFields, isRecordAnswered } from './placeholderPrefill';
import { aiAPI } from '../services/api';
import { ROUTES } from '../constants/constants';

export function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1.5">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#9a9080', animationDelay: `${i * 0.15}s` }} />
      ))}
    </span>
  );
}

// Renders the before/after (or key/value) preview on a proposal card.
function ProposalPreview({ preview }) {
  if (!preview || typeof preview !== 'object') return null;
  if (preview.before && preview.after) {
    return (
      <div className="text-[11.5px] text-[#7a7060] mt-1.5">
        Next filing: <b className="text-[#08111f]">{preview.before.nextDue || '—'}</b> → <b className="text-[#08111f]">{preview.after.nextDue || '—'}</b>
        {' · '}Penalty: ₹{preview.before.totalPenalty || 0} → ₹{preview.after.totalPenalty || 0}
      </div>
    );
  }
  return (
    <div className="text-[11.5px] text-[#7a7060] mt-1.5">
      {Object.entries(preview).map(([k, v]) => `${k}: ${v}`).join(' · ')}
    </div>
  );
}

// A confirm card for an agent action proposal — nothing happens until Confirm.
export function ProposalCard({ proposal, onConfirm }) {
  const [state, setState] = useState('idle'); // idle | confirming | done | error | cancelled
  const [errMsg, setErrMsg] = useState('');

  const confirm = async () => {
    setState('confirming');
    // onConfirm returns true on success, or false / an error-message string on
    // failure — the message stays on the card (a toast is too transient for a
    // multi-line rejection like the recipe smoke test's reasons).
    const r = await onConfirm(proposal);
    if (r === true) {
      setState('done');
    } else {
      setErrMsg(typeof r === 'string' ? r : '');
      setState('error');
    }
  };

  // Restored from history → show a read-only record; never re-execute a stale
  // proposal (its idempotency key + live state are long gone).
  if (proposal.historical) {
    return (
      <div className="rounded-xl border overflow-hidden opacity-90" style={{ borderColor: '#e7d7ad', background: '#fbf7ec' }}>
        <div className="px-3.5 py-2 text-[11px] font-bold flex items-center gap-1.5 border-b" style={{ color: '#8a6a1f', borderColor: '#f0e3c5' }}>
          <FiZap size={13} /> Proposed action (from this conversation)
        </div>
        <div className="px-3.5 py-2.5">
          <div className="text-[12.5px] text-[#08111f]">{proposal.summary}</div>
          <ProposalPreview preview={proposal.preview} />
          <div className="text-[10.5px] text-[#9a9080] mt-1.5">Reopen the relevant page to act on this — historical proposals can’t be confirmed here.</div>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return <div className="text-[12.5px] font-medium rounded-xl px-3.5 py-2.5 flex items-center gap-1.5" style={{ color: '#4a6b3f', background: 'rgba(74,107,63,0.1)' }}><FiCheck size={13} /> {proposal.label || 'Action'} completed.</div>;
  }
  if (state === 'cancelled') {
    return <div className="text-[12px] text-[#9a9080] px-3.5 py-2">Cancelled.</div>;
  }
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#e7d7ad', background: '#fdf8ee' }}>
      <div className="px-3.5 py-2 text-[11px] font-bold flex items-center gap-1.5 border-b" style={{ color: '#8a6a1f', borderColor: '#f0e3c5' }}>
        <FiZap size={13} /> Action needs your confirmation
      </div>
      <div className="px-3.5 py-2.5">
        <div className="text-[12.5px] text-[#08111f]">{proposal.summary}</div>
        <ProposalPreview preview={proposal.preview} />
      </div>
      <div className="px-3.5 py-2.5 border-t" style={{ borderColor: '#f0e3c5' }}>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="xs" onClick={confirm} loading={state === 'confirming'} disabled={state === 'confirming'}>
            Confirm
          </Button>
          <Button variant="ghost" size="xs" onClick={() => setState('cancelled')}>Cancel</Button>
        </div>
        {state === 'error' && (
          <div className="text-[11px] mt-2 leading-relaxed" style={{ color: '#9a564a' }}>
            {errMsg || "Couldn't complete — try again."}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact in-conversation reference to a generated document. It does NOT carry
// actions — clicking it opens the document in the side panel (Claude-style).
export function ArtifactCard({ draft, onOpen, active }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(draft)}
      className={`group w-full text-left rounded-xl border bg-white p-3 flex items-center gap-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_22px_-12px_rgba(20,95,95,0.3)] ${active ? 'border-[#145f5f] ring-1 ring-[#145f5f]' : ''}`}
      style={!active ? { borderColor: '#e2d9c8' } : { borderColor: '#145f5f' }}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 bg-[rgba(20,95,95,0.08)] text-[#145f5f] group-hover:bg-[#145f5f] group-hover:text-white">
        <FiFileText size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-[#08111f] truncate">{draft.title || 'Draft'}</div>
        <div className="text-[10px] uppercase tracking-[0.5px] text-[#9a9080]">
          {draft.kind}
          {draft.fromLibrary
            ? ` · From My Documents${draft.refNumber ? ` · ${draft.refNumber}` : ''}`
            : draft.fromTemplate
              ? ' · Platform template'
              : /fallback/i.test(draft.source || '')
                ? ' · Standard template (AI unavailable)'
                : draft.source === 'targeted-edit'
                  ? ' · Edited by Vee'
                  : ' · AI draft'}
          {draft.edited ? ' · Edited by you' : ''}
        </div>
      </div>
      <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-[#145f5f]">
        {active ? 'Viewing' : 'Open'} <FiExternalLink size={12} />
      </span>
    </button>
  );
}


/**
 * An edited workbook, offered as a download WITH a preview of what changed.
 *
 * The preview is the point, not decoration. These rows are going into a
 * statutory register, and the moment to catch a wrong folio number or a
 * misplaced share range is before the file is saved — not after it has been
 * signed. So the card shows exactly what was written, and the download sits
 * beside it rather than in place of it.
 *
 * The file is fetched through the API client (the /raw route needs the auth
 * header) and handed over as a blob URL: the viewer's browser saves it, and
 * nothing about the workbook has to transit a public link.
 */
export function SpreadsheetEditCard({ edit }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const rows = Array.isArray(edit.preview) ? edit.preview : [];
  const cells = Array.isArray(edit.cellEdits) ? edit.cellEdits : [];
  // Only the columns this edit actually wrote — a register has twenty columns
  // and showing the empty ones buries the three that changed.
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r.values || {})))];

  const download = async () => {
    setBusy(true);
    setFailed('');
    let url = null;
    try {
      const blob = await aiAPI.getChatAttachmentBlob(edit.attachmentId);
      url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = edit.name || 'updated.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      // The bytes age out with the conversation's retention window, so a
      // download that fails later is expected rather than broken — say which
      // it is instead of a bare error.
      setFailed('This file is no longer available to download. Ask Vee to make the edit again.');
    } finally {
      if (url) setTimeout(() => URL.revokeObjectURL(url), 10000);
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#e2d9c8' }}>
      <div className="p-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-[rgba(20,95,95,0.08)] text-[#145f5f]">
          <FiGrid size={15} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-[#08111f] truncate">{edit.name}</div>
          <div className="text-[10px] uppercase tracking-[0.5px] text-[#9a9080] truncate">
            {edit.sheet || (edit.kind === 'document' ? 'Word document' : 'File')}
            {edit.rowsAdded ? ` · ${edit.rowsAdded} row${edit.rowsAdded === 1 ? '' : 's'} added` : ''}
            {edit.cellsChanged
              ? (edit.kind === 'document'
                ? ` · ${edit.cellsChanged} change${edit.cellsChanged === 1 ? '' : 's'}`
                : ` · ${edit.cellsChanged} cell${edit.cellsChanged === 1 ? '' : 's'} changed`)
              : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white transition-opacity disabled:opacity-60"
          style={{ background: '#145f5f' }}
        >
          <FiDownload size={12} /> {busy ? 'Preparing…' : 'Download'}
        </button>
      </div>

      {edit.summary && (
        <div className="px-3 pb-2 -mt-1 text-[11.5px] text-[#5a5245]">{edit.summary}</div>
      )}

      {columns.length > 0 && (
        <div className="border-t overflow-x-auto" style={{ borderColor: '#efe8db' }}>
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr style={{ background: '#faf7f0' }}>
                {columns.map((c) => (
                  <th key={c} className="text-left font-semibold text-[#7a7060] px-2.5 py-1.5 whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.row} className="border-t" style={{ borderColor: '#efe8db' }}>
                  {columns.map((c) => (
                    <td key={c} className="px-2.5 py-1.5 text-[#08111f] align-top">{r.values?.[c] || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {cells.length > 0 && (
        <div className="border-t px-3 py-2 flex flex-col gap-1" style={{ borderColor: '#efe8db' }}>
          {cells.map((c, i) => (
            // A spreadsheet change is anchored to a cell ("D7 100 → 150"); a
            // Word change has no cell, so the before/after text IS the anchor
            // and a missing reference must not render as an empty bold gap.
            <div key={c.cell || `${c.from}-${i}`} className="text-[11px] text-[#5a5245] leading-snug">
              {c.cell && <span className="font-semibold text-[#08111f]">{c.cell} </span>}
              <span className="line-through text-[#9a9080]">{c.from || '(blank)'}</span>
              {' → '}
              <span className="text-[#145f5f] font-medium">{c.to || '(removed)'}</span>
            </div>
          ))}
        </div>
      )}

      <div className="border-t px-3 py-1.5 text-[10px] text-[#9a7a3a] flex items-center gap-1.5" style={{ borderColor: '#efe8db' }}>
        <FiAlertCircle size={11} className="shrink-0" />
        {failed || `Check these entries before filing. Your original ${edit.sourceName || 'file'} is unchanged.`}
      </div>
    </div>
  );
}

// Where each internal source label leads — clicking a source chip takes the
// user to the page that owns that data.
export const SOURCE_ROUTES = {
  'Your companies': ROUTES.COMPANIES,
  'Company deadlines': ROUTES.ALERTS,
  'Portfolio status': ROUTES.DASHBOARD,
  'Applicable forms': ROUTES.MCA_FORMS,
  // Alerts owns the day-wise penalty figures (AGM_CALC only computes dates).
  'Penalty estimate': ROUTES.ALERTS,
  'Professionals': ROUTES.MANAGE_COLLABORATORS,
};

// One clickable web citation. External links use a two-step "arm" pattern:
// first click shows a minor warning, second click opens the site in a new tab.
export function WebSourceLink({ source }) {
  const [armed, setArmed] = useState(false);
  // Web-source URLs come from search grounding (untrusted). Only ever open
  // http(s) — a javascript:/data: URL here would bypass the HTML sanitizer
  // (it's opened, not rendered). Non-http URLs render as plain, un-openable text.
  const parsed = (() => { try { return new URL(source.url); } catch { return null; } })();
  const isWeb = parsed && (parsed.protocol === 'http:' || parsed.protocol === 'https:');
  const host = parsed ? parsed.hostname.replace(/^www\./, '') : source.url;
  const open = () => {
    if (!isWeb) return;
    if (!armed) { setArmed(true); setTimeout(() => setArmed(false), 4000); return; }
    window.open(source.url, '_blank', 'noopener,noreferrer');
    setArmed(false);
  };
  return (
    <button
      type="button"
      onClick={open}
      title={armed ? `Opens ${source.url} in a new tab` : `${source.title} — click twice to open (external site)`}
      className="inline-flex items-center gap-1.5 max-w-full h-6 px-2 rounded-full text-[10.5px] border transition-colors"
      style={armed
        ? { background: 'rgba(176,137,58,0.12)', borderColor: 'rgba(176,137,58,0.4)', color: '#8a6a1f' }
        : { background: 'rgba(20,95,95,0.05)', borderColor: '#e2d9c8', color: '#145f5f' }}
    >
      <FiGlobe size={10} className="shrink-0" />
      <span className="truncate max-w-[220px]">{armed ? `Open ${host}? Click again` : (source.title || host)}</span>
      <FiExternalLink size={10} className="shrink-0" />
    </button>
  );
}

export function MsgAction({ onClick, title, disabled, children }) {
  return (
    <HoverPopover variant="tooltip" content={title}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={title}
        className="w-7 h-7 rounded-lg flex items-center justify-center text-[#9a9080] hover:text-[#145f5f] hover:bg-[#f0ece2] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {children}
      </button>
    </HoverPopover>
  );
}

// Shared shell for every ask-tool card (details confirm, facts form, future
// asks) — one visual language: white card, bold title, muted subtitle, footer
// actions right-aligned. Keep ALL ask cards on this shell.
export function AskCard({ title, subtitle, footer, children, flat = false }) {
  return (
    <div className={flat ? 'p-3.5' : 'rounded-xl border bg-white p-3.5'} style={flat ? undefined : { borderColor: '#e2d9c8' }}>
      <div className="text-[12.5px] font-bold text-[#08111f] mb-0.5">{title}</div>
      {subtitle && <div className="text-[10.5px] mb-2.5" style={{ color: '#9a9080' }}>{subtitle}</div>}
      <div className="space-y-2">{children}</div>
      {footer && <div className="flex items-center justify-between gap-3 mt-3">{footer}</div>}
    </div>
  );
}

// Compact receipt chip left in the transcript once an ask-card completes —
// icon tile + what happened + one detail line, fit-content (never a bland
// full-width bar).
export function AskReceipt({ tone = 'done', title, detail, items = [] }) {
  const done = tone === 'done';
  return (
    <div
      className={`${items.length ? 'flex flex-col' : 'inline-flex items-center'} gap-2.5 rounded-xl border bg-white px-3 py-2 self-start`}
      style={{ borderColor: '#e7e0d0', maxWidth: items.length ? 360 : undefined }}
    >
    <div className="inline-flex items-center gap-2.5">
      <span
        className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
        style={done ? { background: 'rgba(20,95,95,0.12)', color: '#145f5f' } : { background: '#f0ebe0', color: '#7a7060' }}
      >
        {done ? <FiCheck size={13} /> : <FiChevronRight size={13} />}
      </span>
      <span className="min-w-0">
        <span className="block text-[12px] font-semibold leading-tight text-[#08111f]">{title}</span>
        {detail && <span className="block text-[10.5px] leading-tight mt-[2px]" style={{ color: '#9a9080' }}>{detail}</span>}
      </span>
    </div>
      {/* WHAT was taken, not just how many. A deterministic fill produces no
          reply, so without this the transcript's only trace of eight answered
          fields was whatever the user happened to type in "Other". */}
      {items.length > 0 && (
        <div className="flex flex-col gap-[3px] pl-[34px] pb-[2px]">
          {items.map((it) => (
            <span key={it.label} className="text-[11px] leading-snug" style={{ color: '#7a7060' }}>
              {it.label}: <span style={{ color: '#08111f' }}>{it.value}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// DC-2: editable company-details confirmation card. The user verifies (or
// corrects) the values Vee will draft with; corrections become session-scoped
// overrides for this chat — never written to the company record.
const DETAIL_FIELD_LABELS = {
  name: 'Company name',
  address: 'Registered address',
  state: 'State',
  roc: 'ROC',
  auditor: 'Statutory auditor',
  authorisedCapital: 'Authorised capital',
  paidUpCapital: 'Paid-up capital',
};

export function DetailsConfirmCard({ card, onConfirm, disabled, flat }) {
  const [values, setValues] = useState(() => JSON.parse(JSON.stringify(card.values || {})));
  // Fields pre-filled from what the user ALREADY asked for ("make the paid-up
  // capital 10,000") start dirty. They differ from the stored record by
  // definition, so if they didn't count as corrections the user would press
  // "Details are correct", nothing would be sent as changed, and the draft
  // would silently keep the database value — the card showing one number while
  // the document printed another.
  const [dirty, setDirty] = useState(() => new Set(card.proposedFields || []));
  const scalarFields = (card.fields || []).filter((f) => f !== 'directors' && f in DETAIL_FIELD_LABELS);
  const hasDirectors = (card.fields || []).includes('directors');

  const markDirty = (f) => setDirty((prev) => { const next = new Set(prev); next.add(f); return next; });
  const setScalar = (f, v) => { setValues((prev) => ({ ...prev, [f]: v })); markDirty(f); };
  const setDirector = (idx, key, v) => {
    setValues((prev) => ({
      ...prev,
      directors: (prev.directors || []).map((d, i) => (i === idx ? { ...d, [key]: v } : d)),
    }));
    markDirty('directors');
  };

  if (card.confirmed) {
    return (
      <AskReceipt
        title="Company details confirmed"
        detail={`${card.companyName || card.companyCin}${card.confirmedWithEdits ? ' · with your corrections (this chat only)' : ''}`}
      />
    );
  }

  // Scalar rows get the full brand input; the directors grid stays on the
  // compact class — four controls per row at 42px would turn a three-director
  // board into a wall the user has to scroll past.
  const inputCls = inputClass;

  return (
    <AskCard
      flat={flat}
      title={`Confirm details — ${card.companyName || card.companyCin}`}
      subtitle={`These values will be used for the draft${card.hasOverrides ? ' (includes your earlier corrections)' : ''}. Edit anything that’s wrong.`}
      footer={(
        <>
          <span className="text-[10px]" style={{ color: '#a09880' }}>
            Corrections apply to this chat only — edit the company profile for permanent changes.
          </span>
          <Button
            size="sm"
            disabled={disabled}
            onClick={() => {
              const changed = {};
              dirty.forEach((f) => { changed[f] = values[f]; });
              onConfirm(changed, dirty.size > 0);
            }}
          >
            <FiCheck size={13} className="mr-1" /> {dirty.size > 0 ? 'Use corrected details' : 'Details are correct'}
          </Button>
        </>
      )}
    >
      <>
        {scalarFields.map((f) => (
          <div key={f}>
            <label className="block text-[10px] uppercase tracking-[0.5px] font-semibold text-[#9a9080] mb-1">{DETAIL_FIELD_LABELS[f]}</label>
            <input
              className={inputCls}
              style={dirty.has(f) ? { borderColor: '#145f5f' } : undefined}
              value={values[f] || ''}
              placeholder="Not on record — add it"
              onChange={(e) => setScalar(f, e.target.value)}
              disabled={disabled}
            />
          </div>
        ))}

        {hasDirectors && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.5px] font-semibold text-[#9a9080] mb-1">
              Directors {dirty.has('directors') ? '· edited' : ''}
            </label>
            {(values.directors || []).length === 0 ? (
              <div className="text-[11.5px] py-1" style={{ color: '#9a9080' }}>No active directors on record.</div>
            ) : (
              <div className="space-y-1.5">
                {(values.directors || []).map((d, di) => (
                  <div key={di} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: '2fr 1fr 1.2fr auto' }}>
                    <input className={inputSmClass} value={d.name || ''} placeholder="Name" onChange={(e) => setDirector(di, 'name', e.target.value)} disabled={disabled} />
                    <input className={inputSmClass} inputMode="numeric" value={d.din || ''} placeholder="DIN" onChange={(e) => setDirector(di, 'din', e.target.value)} disabled={disabled} />
                    <input className={inputSmClass} value={d.designation || ''} placeholder="Designation" onChange={(e) => setDirector(di, 'designation', e.target.value)} disabled={disabled} />
                    <HoverPopover variant="tooltip" content="Signs documents">
                      <label className="flex items-center gap-1 text-[10.5px] px-1.5" style={{ color: '#7a7060' }}>
                        <input type="checkbox" checked={Boolean(d.signatory)} onChange={(e) => setDirector(di, 'signatory', e.target.checked)} disabled={disabled} />
                        Sign.
                      </label>
                    </HoverPopover>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </>
    </AskCard>
  );
}

// Claude-Code-style structured ask: one form, filled once, instead of prose
// questions that get half-answered. Serves BOTH flows:
//   - request_facts (model asks pre-draft)  → submit composes a reply message
//   - placeholder fill (post-draft)         → submit fills the document
//     client-side with exact replacement — no AI involved at all
// QA-3.2: with web search off, Vee closes dated or unverifiable answers with an
// offer to verify ("enable web search for this chat…" — the system prompt
// mandates the phrasing). This spots the offer so the dock can turn the
// two-step suggestion into one tap; message-level `webOfferHandled` retires it.
export const WEB_VERIFY_OFFER_RE = /\b(?:enable|turn on|allow)\b[^.!?\n]{0,40}?\bweb search\b/i;

export function WebSearchOfferCard({ onAllow, onDismiss, flat }) {
  return (
    <AskCard
      flat={flat}
      title="Vee can verify this on the web"
      subtitle="Web search is off for this chat. Allow it and Vee will re-check the answer against current sources."
      footer={(
        <>
          <span />
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="text-[11.5px] font-semibold px-3 h-8 rounded-lg transition-colors hover:bg-[#f6f3ea]"
              style={{ color: '#7a7060' }}
              onClick={() => onDismiss?.()}
            >
              Not now
            </button>
            <Button size="sm" onClick={() => onAllow?.()}>
              <FiGlobe size={13} className="mr-1" /> Allow for this chat
            </Button>
          </span>
        </>
      )}
    />
  );
}

// Which input box each fact gets — a date picker, a rupee box, a director
// list — is decided by `field.type`, which the MODEL sets on the request_facts
// tool call (see chat/factsFields.jsx for the closed registry, and
// backend .../tools/drafting.js for the enum it must come from). Free text let
// anything through ("dsad", "sometime next week") and produced ambiguous
// formats in statutory documents.

// A prefill that is itself a bracketed token ("[DATE]") is a leak, not a
// value — seeding it would suppress the picker and put the token in the box.
const cleanPrefill = (v) => (/\[[^\]]*\]/.test(String(v || '')) ? '' : v);

export function FactsFormCard({ card, onSubmit, onSkip, disabled, flat }) {
  // Fields carrying a `value` were resolved from the company record (see
  // placeholderPrefill) — seed them so the user edits rather than retypes.
  const [values, setValues] = useState(() =>
    Object.fromEntries((card.fields || []).filter((f) => cleanPrefill(f.value)).map((f) => [f.key, cleanPrefill(f.value)])));
  // No free-text box here on purpose. The composer directly below this card
  // already says "Or type here instead — this skips the questions above", so a
  // second box inside the form was a second door to the same place: it made the
  // form longer, and anything typed in it went to Vee as a separate message
  // while the fields went silently into the document — two halves of one answer
  // arriving by different routes. Typed instructions belong in the composer.
  // Fields the user has typed in / chosen — never overwritten by a late prefill.
  const touched = useRef({});

  // The company list loads ASYNCHRONOUSLY, so the first render of this card
  // frequently has no resolved company yet and therefore no prefilled values.
  // useState's initializer runs once, so without this the form would stay
  // permanently blank for anyone whose draft rendered before the companies
  // arrived. Adopt values as they appear, but only into fields that are still
  // empty and untouched.
  const prefillSignature = (card.fields || []).map((f) => `${f.key}=${f.value || ''}`).join('|');
  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const f of card.fields || []) {
        const v = cleanPrefill(f.value);
        if (v && !touched.current[f.key] && !String(prev[f.key] || '').trim()) {
          next[f.key] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillSignature]);

  // EVERY key in the patch counts as touched, not just the one edited: picking
  // a director also fills their DIN, and a late prefill must not undo either
  // half of that pair.
  const setField = (patch) => {
    Object.keys(patch).forEach((k) => { touched.current[k] = true; });
    setValues((prev) => ({ ...prev, ...patch }));
  };
  if (card.completed) {
    const skipped = /skip/i.test(card.completedNote || '');
    // The values, listed. A count alone ("8 details added") tells the user
    // something happened without telling them WHAT, which is the same gap the
    // vanished form left behind.
    const answered = Object.entries(card.answers || {})
      .map(([key, value]) => {
        const field = (card.fields || []).find((f) => f.key === key);
        return { label: (field && field.label) || placeholderLabel(key), value: String(value || '').trim() };
      })
      .filter((it) => it.value)
      .slice(0, 12);
    return skipped ? (
      <AskReceipt tone="muted" title="Questions skipped" detail={card.completedDetail || 'Vee proceeded with placeholders for the rest'} />
    ) : (
      <AskReceipt
        title={card.title || 'Details submitted'}
        detail={card.completedDetail || 'Used for the draft'}
        items={answered}
      />
    );
  }
  // What the user still has to answer, versus what the platform already knows.
  // A record-answered field keeps its VALUE (it still reaches the document) —
  // it just stops occupying a box the user has to read and skip past.
  const asked = askableFields(card.fields || []);
  const fromRecord = (card.fields || []).filter(isRecordAnswered);
  const filledCount = Object.values(values).filter((v) => String(v || '').trim()).length;
  const canSubmit = filledCount > 0;
  return (
    <AskCard
      flat={flat}
      title={card.title || 'A few details needed'}
      subtitle={card.subtitle || 'Fill what you have — anything left blank stays as a placeholder.'}
      footer={(
        <>
          <span />
          <span className="flex items-center gap-2">
            <button
              type="button"
              className="text-[11.5px] font-semibold px-3 h-8 rounded-lg transition-colors hover:bg-[#f6f3ea]"
              style={{ color: '#7a7060' }}
              disabled={disabled}
              onClick={() => onSkip?.()}
            >
              Skip
            </button>
            <Button
              size="sm"
              disabled={disabled || !canSubmit}
              onClick={() => {
                // Convert picker values to document-ready form on the way out:
                // yyyy-mm-dd → "5 August 2026", HH:MM → "11:00 A.M.",
                // 1500000 → "₹15,00,000".
                const converted = Object.fromEntries(Object.entries(values).map(([k, v]) => {
                  const field = (card.fields || []).find((f) => f.key === k);
                  return [k, field ? toDocumentValue(field, v) : v];
                }));
                onSubmit(converted, '');
              }}
            >
              <FiCheck size={13} className="mr-1" /> Use these details
            </Button>
          </span>
        </>
      )}
    >
      {asked.map((f) => (
        <div key={f.key}>
          <label className="block text-[10px] uppercase tracking-[0.5px] font-semibold text-[#9a9080] mb-1">{f.label}</label>
          <FactsField field={f} value={values[f.key]} onChange={setField} disabled={disabled} />
        </div>
      ))}
      {/* Said, not hidden. The values still go into the document; the user is
          told where they came from so a wrong one on the company profile is
          findable rather than mysterious. */}
      {fromRecord.length > 0 && (
        <p className="text-[10.5px] leading-relaxed" style={{ color: '#7a7060' }}>
          Taken from your company record:{' '}
          <span style={{ color: '#145f5f' }}>{fromRecord.map((f) => f.label).join(', ')}</span>.
          {' '}Edit the company profile to change them.
        </p>
      )}
    </AskCard>
  );
}

// Humanize "[Day of Meeting]" → "Day of Meeting" for the fill form's labels.
export const placeholderLabel = (ph) => String(ph || '').replace(/^\[|\]$/g, '');
